// The legend words for the booking grids (X47; Grant 2026-10-19: "when hovering
// over a legend entry it needs to explain what it means, or on mobile have a
// dialog explaining each item"). One list per surface, each entry one sentence,
// so the tooltips on a mouse and the dialog on a phone never differ. The member
// book page uses MEMBER_LEGEND; the admin calendar and the Bookings page pass
// their own lists to the same <booking-legend> component (X49).
//
import {CELL_STATES} from '~/utils/bookingWeek';
import {AREA_NOUN} from '~/utils/areaWords';

//   {key, cls, word, sentence, icon?}
//   cls   → the swatch's class suffix (the grid's own cell classes draw it)
//   word  → the short label beside the swatch
//   sentence → what it means, in plain words (tooltip and dialog)

// X58 (R23a M1, Grant: "they MUST match the cells"): one entry per cell STATE, its class and icon read from CELL_STATES
// itself, so a Can't book swatch is the icon of the cells it stands beside (card-off, lock, whistle…) and never one fixed
// icon for the family. The page lists only the states on screen (legendKeys).
const cant = (key, word, sentence) => ({key, cls: CELL_STATES[key].cls, icon: CELL_STATES[key].icon ?? null, word, sentence});

// Grant 2026-11-02: said with the tab's own noun ("closed this court", "this court or board"), "playing area" only when none is known.
export const memberLegend = (noun = AREA_NOUN) => Object.freeze([
  {key: 'free', cls: 'free', word: 'Available', sentence: 'Available: you can book this slot. It shows its price, or Free when it costs nothing. Tap it to book.'},
  {key: 'taken', cls: 'taken', word: 'Taken', sentence: 'Taken: someone else has booked it, or a match is on.'},
  {key: 'mine', cls: 'mine', word: 'Yours', sentence: "Yours: your own booking. Change or cancel it from Your bookings."},
  {key: 'open', cls: 'open', word: 'Open play', sentence: 'Open play: no booking needed, just turn up and play.', icon: 'mdi-walk'},
  cant('noPayments', "Can't book", "Can't book: this club can't take card payments online right now, so a priced time can't be booked here. Ask the club how to pay."),
  cant('limit', 'At limit', "At limit: you have every booking you're allowed coming up. Cancel one to book another."),
  cant('noAccess', "Can't book", "Can't book: you can't book this time. Hover or tap a cell to see why."),
  cant('notYetOpen', 'Not yet', "Not yet: beyond how far ahead you can book. It opens later."),
  cant('matchesOnly', 'Matches only', 'Matches only: this time is kept for league and ladder matches.'),
  cant('frozen', 'Frozen', `Frozen: the club has closed this ${noun} for a while.`),
  cant('closed', 'Closed', `Closed: the club doesn't open this ${noun} then.`),
]);

/** The member legend with no noun known (the fallback words). */
export const MEMBER_LEGEND = memberLegend();

/** The entry for a key, or null. */
export function legendEntry(items, key) {
  return (items ?? []).find((l) => l.key === key) ?? null;
}

/** The dialog's heading. */
export const LEGEND_TITLE = 'What the colours mean';

/** The control beside the legend that opens the dialog. */
export const LEGEND_HELP = 'What do these mean?';

/** Every sentence starts with its word and a colon, so the two never drift apart. */
export function legendConsistent(items) {
  return (items ?? []).every((l) => typeof l.sentence === 'string' && l.sentence.startsWith(`${l.word}:`));
}
